import { useState, useEffect } from 'react';
import { employeeService } from '../services/employee.service';
import { EmployeePosition } from '../types/employee.types';

interface UsePositionsReturn {
  positions: EmployeePosition[];
  loading: boolean;
  error: string | null;
  refreshPositions: () => Promise<void>;
}

export const usePositions = (): UsePositionsReturn => {
  const [positions, setPositions] = useState<EmployeePosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPositions = async () => {
    try {
      console.log('🔍 usePositions: Fetching positions from API...');
      setLoading(true);
      setError(null);
      const response = await employeeService.getPositions();
      console.log('✅ usePositions: API Response:', response);
      setPositions(response.data || []);
    } catch (err) {
      console.error('❌ usePositions: Error fetching positions:', err);
      setError('Error al cargar las posiciones');
      // Fallback to empty array if there's an error
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  const refreshPositions = async () => {
    await fetchPositions();
  };

  return {
    positions,
    loading,
    error,
    refreshPositions
  };
};

// Helper function to get position options for dropdowns
export const usePositionOptions = () => {
  const { positions, loading, error } = usePositions();

  console.log('🎯 usePositionOptions: positions=', positions, 'loading=', loading, 'error=', error);

  const positionOptions = [
    { value: 'all', label: 'Todos los cargos' },
    ...positions.map(position => ({
      value: position.name,
      label: position.name
    }))
  ];

  console.log('📋 usePositionOptions: Generated options=', positionOptions);

  return {
    positionOptions,
    positions,
    loading,
    error
  };
};

// Helper function to get position color based on level
export const getPositionColor = (positionLevel: string) => {
  const levelColors = {
    'admin': 'bg-purple-50 text-purple-600',
    'executive': 'bg-red-50 text-red-600',
    'manager': 'bg-blue-50 text-blue-600',
    'cashier': 'bg-green-50 text-green-600',
    'cook': 'bg-orange-50 text-orange-600',
    'service': 'bg-yellow-50 text-yellow-600',
    'support': 'bg-gray-50 text-gray-600'
  };

  return levelColors[positionLevel as keyof typeof levelColors] || 'bg-gray-50 text-gray-600';
};